import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Download,
  Check,
  FileText,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Sparkles,
  Minus,
  Plus,
  Search,
  X,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { fetchExamDDetail, fetchExamGrades } from "../api/client";
import type { ExamDDetail, ExamDSubmission, SessionGrades } from "../api/types";
import { useTranslation } from "react-i18next";

// ── Types ────────────────────────────────────────────────────────────────────

type GradeEntry = SessionGrades[number];
type StudentStatus = "graded" | "review" | "pending";
type QuestionVerdict = "correct" | "partial" | "wrong";

interface EnrichedStudent {
  submission: ExamDSubmission;
  grade: GradeEntry | null;
  status: StudentStatus;
  totalScore: number | null;
  totalMax: number;
  pct: number | null;
}

interface QuestionJson {
  question_number: number | string;
  question_text?: string;
  reference_answer?: string;
  maximum_points?: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function streamFileUrl(fileId: string) {
  return `/stream_file/${fileId}`;
}

/** Optional data source used by the self-contained demo (no API calls). */
export interface ExamDSideBySideDemoData {
  exam: ExamDDetail;
  grades: SessionGrades;
  /** Maps a submission file id to an image URL. Defaults to /stream_file/<id>. */
  resolveFileUrl?: (fileId: string) => string;
}

function computeStatus(grade: GradeEntry | null): StudentStatus {
  if (!grade?.paper_grade) return "pending";
  const hasLowConf =
    grade.paper_grade.has_low_confidence ||
    grade.question_grades.some((q) => q.ocr_confidence_is_high === false);
  return hasLowConf ? "review" : "graded";
}

function computeTotals(grade: GradeEntry | null) {
  const totalMax = grade?.question_grades.reduce((s, q) => s + (q.max_score ?? 0), 0) ?? 0;
  const totalScore = grade?.paper_grade?.total_score ?? null;
  const pct = totalMax > 0 && totalScore !== null ? Math.round((totalScore / totalMax) * 100) : null;
  return { totalScore, totalMax, pct };
}

function questionVerdict(score: number, maxScore: number): QuestionVerdict {
  if (score >= maxScore) return "correct";
  if (score <= 0) return "wrong";
  return "partial";
}

function ringColor(pct: number | null): string {
  if (pct === null) return "var(--border-color)";
  if (pct >= 80) return "var(--color-success, #22c55e)";
  if (pct >= 50) return "var(--color-warning, #f59e0b)";
  return "var(--color-danger, #ef4444)";
}

// ── Score Ring ───────────────────────────────────────────────────────────────

function ScoreCircle({ pct, size = 52 }: { pct: number | null; size?: number }) {
  const r = (size - 5) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - ((pct ?? 0) / 100) * circ;
  const color = ringColor(pct);
  const cx = size / 2;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--sbs-muted-bg)" strokeWidth={5} />
        <circle
          cx={cx} cy={cx} r={r} fill="none"
          stroke={color} strokeWidth={5}
          strokeLinecap="round"
          strokeDasharray={`${circ - offset} ${offset}`}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "var(--font-mono, monospace)",
        fontSize: size > 44 ? "0.9rem" : "0.7rem",
        fontWeight: 600,
        color,
        lineHeight: 1,
      }}>
        {pct ?? "—"}
      </div>
    </div>
  );
}

// ── Student List ─────────────────────────────────────────────────────────────

function StudentList({
  students,
  selectedId,
  onSelect,
  collapsed,
  onToggle,
}: {
  students: EnrichedStudent[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation("pages");
  const [query, setQuery] = useState("");
  const filtered = query.trim()
    ? students.filter((s) =>
        s.submission.student_name.toLowerCase().includes(query.toLowerCase()) ||
        s.submission.student_no?.toLowerCase().includes(query.toLowerCase())
      )
    : students;

  const gradedCount = students.filter((s) => s.status !== "pending").length;

  if (collapsed) {
    return (
      <div className="sbs-student-col sbs-student-col--collapsed">
        <button className="sbs-roster-toggle" onClick={onToggle} title={t("examDSideBySide.showList")}>
          <ChevronRight size={16} />
        </button>
        <span className="sbs-roster-collapsed-badge">{gradedCount}/{students.length}</span>
      </div>
    );
  }

  return (
    <div className="sbs-student-col">
      <div className="sbs-student-col-header">
        <span className="sbs-student-col-title">{t("examDSideBySide.studentsTitle")}</span>
        <span className="sbs-count-badge">{gradedCount}/{students.length}</span>
        <button className="sbs-roster-toggle sbs-roster-toggle--inline" onClick={onToggle} title={t("examDSideBySide.hideList")}>
          <ChevronLeft size={16} />
        </button>
      </div>
      <div className="sbs-search-wrapper">
        <Search className="sbs-search-icon" size={14} />
        <input
          className="sbs-search-input"
          placeholder={t("examDSideBySide.searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <ul className="sbs-student-list">
        {filtered.map((s, idx) => {
          const isActive = s.submission.id === selectedId;
          const scoreColor =
            s.pct === null ? "var(--text-muted)"
            : s.pct >= 80 ? "var(--color-success, #22c55e)"
            : s.pct >= 50 ? "var(--color-warning, #f59e0b)"
            : "var(--color-danger, #ef4444)";
          return (
            <li key={s.submission.id}>
              <button
                className={`sbs-student-item${isActive ? " sbs-student-item--active" : ""}`}
                onClick={() => onSelect(s.submission.id)}
              >
                <span className={`sbs-student-num${isActive ? " sbs-student-num--active" : ""}`}>
                  {idx + 1}
                </span>
                <span className="sbs-student-info">
                  <span className="sbs-student-name">{s.submission.student_name}</span>
                  <span className="sbs-student-status-row">
                    <span className={`sbs-dot sbs-dot--${s.status}`} />
                    <span className="sbs-student-status-label">
                      {s.status === "graded"
                        ? t("examDSideBySide.statusApproved")
                        : s.status === "review"
                        ? t("examDSideBySide.statusReview")
                        : t("examDSideBySide.statusPending")}
                    </span>
                  </span>
                </span>
                {s.pct !== null ? (
                  <span className="sbs-student-score" style={{ color: scoreColor }}>
                    {s.pct}
                  </span>
                ) : (
                  <span className="sbs-student-score sbs-student-score--empty">—</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Content Header ───────────────────────────────────────────────────────────

function ContentHeader({
  student,
  exam,
  index,
  total,
  onPrev,
  onNext,
  paperVisible,
  onTogglePaper,
}: {
  student: EnrichedStudent | null;
  exam: ExamDDetail;
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  paperVisible: boolean;
  onTogglePaper: () => void;
}) {
  const { t } = useTranslation("pages");
  if (!student) return null;
  return (
    <div className="sbs-content-header">
      <ScoreCircle pct={student.pct} size={52} />
      <div className="sbs-header-info">
        <span className="sbs-header-name">{student.submission.student_name}</span>
        <span className="sbs-header-meta">
          {exam.exam_name}
          {exam.subject ? ` · ${exam.subject}` : ""}
          {exam.grade_level ? ` · ${exam.grade_level}` : ""}
        </span>
      </div>
      {/* Prev / Next */}
      <div className="sbs-pagination">
        <button className="sbs-pag-btn" onClick={onPrev} disabled={index === 0}>
          <ChevronLeft size={16} />
        </button>
        <span className="sbs-pag-count">{index + 1}/{total}</span>
        <button className="sbs-pag-btn" onClick={onNext} disabled={index === total - 1}>
          <ChevronRight size={16} />
        </button>
      </div>
      {/* Actions */}
      <div className="sbs-header-actions">
        <button
          className="sbs-btn-toggle"
          onClick={onTogglePaper}
          title={paperVisible ? t("examDSideBySide.hidePaper") : t("examDSideBySide.showPaper")}
          aria-pressed={!paperVisible}
        >
          {paperVisible ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
          <span>{paperVisible ? t("examDSideBySide.hidePaperLabel") : t("examDSideBySide.showPaperLabel")}</span>
        </button>
        <button className="sbs-btn-export">
          <Download size={14} />
          <span>{t("examDSideBySide.exportLabel")}</span>
        </button>
        <button className="sbs-btn-approve">
          <Check size={14} />
          {t("examDSideBySide.approveLabel")}
        </button>
      </div>
    </div>
  );
}

// ── Paper Viewer ─────────────────────────────────────────────────────────────

function PaperViewer({
  submission,
  resolveFileUrl,
}: {
  submission: ExamDSubmission;
  resolveFileUrl?: (fileId: string) => string;
}) {
  const { t } = useTranslation("pages");
  const [pageIdx, setPageIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [imgError, setImgError] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);

  // Reset when student or page changes
  useEffect(() => { setPageIdx(0); setZoom(1); }, [submission.id]);
  useEffect(() => { setImgError(false); setImgLoading(true); }, [submission.id, pageIdx]);

  const fileIds = submission.file_ids ?? [];
  const currentFid = fileIds[pageIdx] ?? null;
  const zoomPct = Math.round(zoom * 100);

  return (
    <>
      <div className="sbs-paper-col">
        {/* Toolbar */}
        <div className="sbs-viewer-toolbar">
          <div className="sbs-toolbar-left">
            <FileText size={14} className="sbs-toolbar-icon" />
            <span className="sbs-toolbar-label">
              {t("examDSideBySide.paperTitle", { studentName: submission.student_name })}
            </span>
          </div>
          <div className="sbs-toolbar-right">
            <button className="sbs-icon-btn" onClick={() => setZoom((z) => Math.max(0.3, z - 0.1))} title={t("examDSideBySide.minimise")}>
              <ZoomOut size={14} />
            </button>
            <span className="sbs-zoom-pct">{zoomPct}%</span>
            <button className="sbs-icon-btn" onClick={() => setZoom((z) => Math.min(3, z + 0.1))} title={t("examDSideBySide.maximise")}>
              <ZoomIn size={14} />
            </button>
            <div className="sbs-toolbar-divider" />
            <button className="sbs-icon-btn" onClick={() => setZoom(1)} title={t("examDSideBySide.resetZoom")}>
              <RotateCw size={14} />
            </button>
          </div>
        </div>

        {/* Image area */}
        <div className="sbs-image-area">
          {!currentFid ? (
            <div className="sbs-image-placeholder">
              <FileText size={36} className="sbs-placeholder-icon" />
              <span className="sbs-placeholder-title">{t("examDSideBySide.paperNotFound")}</span>
              <span className="sbs-placeholder-sub">{t("examDSideBySide.noPaperFiles")}</span>
            </div>
          ) : imgError ? (
            <div className="sbs-image-placeholder">
              <FileText size={36} className="sbs-placeholder-icon" />
              <span className="sbs-placeholder-title">{t("examDSideBySide.paperLoadError")}</span>
              <span className="sbs-placeholder-sub">{t("examDSideBySide.fileNotViewable")}</span>
            </div>
          ) : (
            <div className="sbs-image-inner">
              {imgLoading && (
                <div className="sbs-img-loading-overlay">
                  <span className="sbs-img-loading-dot" />
                  <span className="sbs-img-loading-dot" />
                  <span className="sbs-img-loading-dot" />
                </div>
              )}
              <img
                src={resolveFileUrl ? resolveFileUrl(currentFid) : streamFileUrl(currentFid)}
                alt={t("examDSideBySide.imageAlt", { studentName: submission.student_name, n: pageIdx + 1 })}
                className="sbs-page-img"
                style={{
                  // "contain" keeps the full page visible; zoom grows the box so
                  // the viewer scrolls instead of squeezing the image.
                  width: `${zoom * 100}%`,
                  height: `${zoom * 100}%`,
                  opacity: imgLoading ? 0 : 1,
                  transition: "opacity 0.2s",
                }}
                onClick={() => setZoom((z) => Math.min(3, z + 0.4))}
                onLoad={() => setImgLoading(false)}
                onError={() => { setImgError(true); setImgLoading(false); }}
              />
            </div>
          )}
        </div>

        {/* Page tabs */}
        {fileIds.length > 1 && (
          <div className="sbs-page-tabs">
            {fileIds.map((_, i) => (
              <button
                key={i}
                className={`sbs-page-tab${i === pageIdx ? " sbs-page-tab--active" : ""}`}
                onClick={() => setPageIdx(i)}
              >
                {t("examDSideBySide.pageTab", { n: i + 1 })}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ── Grading Panel ─────────────────────────────────────────────────────────────

function GradingPanel({
  grade,
  exam,
}: {
  grade: GradeEntry | null;
  exam: ExamDDetail;
}) {
  const { t } = useTranslation("pages");
  // Local score overrides (+ / - stepper)
  const [scoreOverrides, setScoreOverrides] = useState<Record<number, number>>({});
  // Collapsed state for student answer sections (default: all collapsed)
  const [expandedAnswers, setExpandedAnswers] = useState<Record<number, boolean>>({});
  // Feedback edit state — which question is being edited and its draft text
  const [editingId, setEditingId] = useState<number | null>(null);
  const [feedbackDraft, setFeedbackDraft] = useState("");
  // Saved feedback overrides
  const [feedbackOverrides, setFeedbackOverrides] = useState<Record<number, string>>({});
  // Raw data modal
  const [rawDataModal, setRawDataModal] = useState<GradeEntry["question_grades"][number] | null>(null);

  useEffect(() => {
    setScoreOverrides({});
    setExpandedAnswers({});
    setEditingId(null);
    setFeedbackDraft("");
    setFeedbackOverrides({});
  }, [grade]);

  // Build question text lookup from exam.questions_json
  const questionTexts = useRef<Record<string, string>>({});
  const questionRubric = useRef<Record<string, string>>({});
  useEffect(() => {
    const textMap: Record<string, string> = {};
    const rubricMap: Record<string, string> = {};
    for (const q of (exam.questions_json ?? []) as QuestionJson[]) {
      if (q.question_number != null) {
        textMap[String(q.question_number)] = q.question_text ?? "";
        if (q.reference_answer) {
          rubricMap[String(q.question_number)] = q.reference_answer;
        }
      }
    }
    questionTexts.current = textMap;
    questionRubric.current = rubricMap;
  }, [exam.questions_json]);

  function startEdit(id: number, currentText: string) {
    setEditingId(id);
    setFeedbackDraft(currentText);
  }

  function cancelEdit() {
    setEditingId(null);
    setFeedbackDraft("");
  }

  function saveEdit(id: number) {
    setFeedbackOverrides((prev) => ({ ...prev, [id]: feedbackDraft }));
    setEditingId(null);
    setFeedbackDraft("");
  }

  if (!grade) {
    return (
      <div className="sbs-grading-col">
        <div className="sbs-grading-header">
          <div className="sbs-grading-header-left">
            <Sparkles size={14} className="sbs-toolbar-icon" />
            <span className="sbs-toolbar-label">{t("examDSideBySide.aiAssessment")}</span>
          </div>
        </div>
        <div className="sbs-grading-empty">
          <span className="sbs-muted-text">{t("examDSideBySide.notGradedYet")}</span>
        </div>
      </div>
    );
  }

  const qGrades = grade.question_grades;

  return (
    <div className="sbs-grading-col">
      {/* Panel header */}
      <div className="sbs-grading-header">
        <div className="sbs-grading-header-left">
          <Sparkles size={14} className="sbs-toolbar-icon" />
          <span className="sbs-toolbar-label">{t("examDSideBySide.aiAssessment")}</span>
        </div>
      </div>

      {/* Question cards */}
      <div className="sbs-cards-scroll">
        {qGrades.map((qg) => {
          const currentScore = scoreOverrides[qg.id] ?? qg.score;
          const displayLabel = qg.question_display ?? `${qg.question_number}`;
          const qText = questionTexts.current[String(qg.question_number)] ?? "";
          const isExpanded = expandedAnswers[qg.id] ?? false;
          const isEditing = editingId === qg.id;
          const feedbackText = feedbackOverrides[qg.id] ?? qg.feedback;

          return (
            <article key={qg.id} className="sbs-q-card">
              {/* Card header: question label + stepper */}
              <div className="sbs-qc-header">
                <button
                  className="sbs-qc-num sbs-qc-num--btn"
                  onClick={() => setRawDataModal(qg)}
                  title={t("examDSideBySide.showRawData")}
                >
                  {displayLabel}
                </button>
                {qText && <span className="sbs-qc-text">{qText}</span>}
                <div className="sbs-stepper">
                  <button
                    className="sbs-stepper-btn"
                    onClick={() =>
                      setScoreOverrides((prev) => ({
                        ...prev,
                        [qg.id]: Math.max(0, (prev[qg.id] ?? qg.score) - 1),
                      }))
                    }
                  >
                    <Minus size={14} />
                  </button>
                  <span className="sbs-stepper-val">
                    {currentScore}
                    <span className="sbs-stepper-max">/{qg.max_score}</span>
                  </span>
                  <button
                    className="sbs-stepper-btn"
                    onClick={() =>
                      setScoreOverrides((prev) => ({
                        ...prev,
                        [qg.id]: Math.min(qg.max_score, (prev[qg.id] ?? qg.score) + 1),
                      }))
                    }
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              {/* Student answer — collapsible */}
              {qg.student_answer != null && (
                <div className="sbs-student-answer-wrap">
                  {!isExpanded ? (
                    <button
                      className="sbs-student-answer-toggle"
                      onClick={() =>
                        setExpandedAnswers((prev) => ({ ...prev, [qg.id]: true }))
                      }
                    >
                      <span className="sbs-student-answer-toggle-label">{t("examDSideBySide.studentAnswer")}</span>
                      <ChevronDown size={14} className="sbs-student-answer-chevron" />
                    </button>
                  ) : (
                    <div
                      className="sbs-student-answer-body sbs-student-answer-body--expanded"
                      onClick={() =>
                        setExpandedAnswers((prev) => ({ ...prev, [qg.id]: false }))
                      }
                      title={t("examDSideBySide.clickToClose")}
                    >
                      {qg.student_answer || <em className="sbs-muted-text">{t("examDSideBySide.emptyAnswer")}</em>}
                    </div>
                  )}
                </div>
              )}

              {/* Rubric / expected answer box (if available) */}
              {questionRubric.current[String(qg.question_number)] && (
                <div className="sbs-rubric-box">
                  <span className="sbs-rubric-label">{t("common.referenceAnswer")}</span>
                  {questionRubric.current[String(qg.question_number)]}
                </div>
              )}

              {/* AI feedback — click to edit */}
              <div className="sbs-qc-feedback">
                <Sparkles size={14} className="sbs-qc-feedback-icon" />
                {isEditing ? (
                  <textarea
                    className="sbs-feedback-textarea"
                    value={feedbackDraft}
                    autoFocus
                    onChange={(e) => setFeedbackDraft(e.target.value)}
                    onBlur={() => saveEdit(qg.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                    }}
                  />
                ) : (
                  <p
                    className="sbs-qc-feedback-text sbs-qc-feedback-text--editable"
                    onClick={() => startEdit(qg.id, feedbackText)}
                    title={t("common.clickToEdit")}
                  >
                    {feedbackText}
                  </p>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {/* Full-width approve */}
      <div className="sbs-approve-bar">
        <button className="sbs-approve-btn">
          <Check size={16} />
          {t("examDSideBySide.approveAll")}
        </button>
      </div>

      {/* Raw data modal */}
      {rawDataModal && (
        <div className="sbs-raw-backdrop" onClick={() => setRawDataModal(null)}>
          <div className="sbs-raw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sbs-raw-modal-header">
              <span className="sbs-raw-modal-title">
                {t("examDSideBySide.rawDataTitle", { n: rawDataModal.question_display ?? rawDataModal.question_number })}
              </span>
              <button className="sbs-raw-close" onClick={() => setRawDataModal(null)}>
                <X size={16} />
              </button>
            </div>
            <pre className="sbs-raw-pre">
              {JSON.stringify(rawDataModal, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ExamDSideBySide({ demo }: { demo?: ExamDSideBySideDemoData }) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation("pages");

  const [exam, setExam] = useState<ExamDDetail | null>(demo?.exam ?? null);
  const [grades, setGrades] = useState<SessionGrades>(demo?.grades ?? []);
  const [loading, setLoading] = useState(!demo);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(demo?.exam.submissions?.[0]?.id ?? null);
  const [rosterCollapsed, setRosterCollapsed] = useState(false);
  const [paperVisible, setPaperVisible] = useState(true);

  useEffect(() => {
    // Demo mode: render the fixtures handed in, never hit the API.
    if (demo) {
      setExam(demo.exam);
      setGrades(demo.grades);
      setSelectedId(demo.exam.submissions?.[0]?.id ?? null);
      setError(null);
      setLoading(false);
      return;
    }
    if (!sessionId) return;
    setLoading(true);
    setError(null);

    fetchExamDDetail(sessionId)
      .then((examData) => {
        setExam(examData);
        const runId = examData.latest_run?.id ?? null;
        return fetchExamGrades(sessionId, runId ?? undefined).then((g) => {
          setGrades(g);
          if (examData.submissions?.length) setSelectedId(examData.submissions[0].id);
        });
      })
      .catch((err: any) => setError(err?.response?.data?.error || err?.message || t("examDSideBySide.loadError")))
      .finally(() => setLoading(false));
  }, [demo, sessionId]);

  if (loading) {
    return <div className="sbs-state-screen"><span className="sbs-muted-text">{t("examDSideBySide.loading")}</span></div>;
  }
  if (error || !exam) {
    return (
      <div className="sbs-state-screen">
        <span className="sbs-muted-text">{error ?? t("examDSideBySide.examNotFound")}</span>
        <button className="sbs-btn-export" onClick={() => navigate(-1)}>{t("common.back")}</button>
      </div>
    );
  }

  // Build enriched student list
  const students: EnrichedStudent[] = (exam.submissions ?? []).map((sub) => {
    const grade = grades.find((g) => g.student_name === sub.student_name) ?? null;
    const { totalScore, totalMax, pct } = computeTotals(grade);
    return { submission: sub, grade, status: computeStatus(grade), totalScore, totalMax, pct };
  });

  const selectedIdx = students.findIndex((s) => s.submission.id === selectedId);
  const selected = selectedIdx >= 0 ? students[selectedIdx] : null;

  const goTo = (idx: number) => {
    if (idx >= 0 && idx < students.length) setSelectedId(students[idx].submission.id);
  };

  return (
    <div className="sbs-root animate-fade-in">
      {/* Left: student list */}
      <StudentList
        students={students}
        selectedId={selectedId}
        onSelect={setSelectedId}
        collapsed={rosterCollapsed}
        onToggle={() => setRosterCollapsed((v) => !v)}
      />

      {/* Right: main area */}
      <div className="sbs-main">
        <ContentHeader
          student={selected}
          exam={exam}
          index={selectedIdx}
          total={students.length}
          onPrev={() => goTo(selectedIdx - 1)}
          onNext={() => goTo(selectedIdx + 1)}
          paperVisible={paperVisible}
          onTogglePaper={() => setPaperVisible((v) => !v)}
        />
        <div className={`sbs-split${paperVisible ? "" : " sbs-split--single"}`}>
          {paperVisible &&
            (selected ? (
              <PaperViewer submission={selected.submission} resolveFileUrl={demo?.resolveFileUrl} />
            ) : (
              <div className="sbs-paper-col sbs-image-empty">
                <span className="sbs-muted-text">{t("examDSideBySide.selectStudent")}</span>
              </div>
            ))}
          <GradingPanel grade={selected?.grade ?? null} exam={exam} />
        </div>
      </div>
    </div>
  );
}
